import { Box, IconButton, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import type { PropsWithChildren, ReactNode } from 'react'
import { YALO_RADIUS, YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'

/**
 * The shell every screen sits in.
 *
 * The design is drawn for a phone — this storefront opens inside a chat
 * webview — so the column is capped and centred rather than stretched. On a
 * desktop browser that reads as a wide app with a comfortable measure, which is
 * what the reference shows.
 *
 * `data-testid` is passed through because the router test asserts on it, and
 * because a screen that cannot be found in a test is a screen nobody will
 * refactor safely.
 */
export const Screen = ({
  testId,
  children,
}: PropsWithChildren<{ testId: string }>) => (
  <Box
    component="main"
    data-testid={testId}
    sx={{
      minHeight: '100dvh',
      bgcolor: YALO_SEMANTIC.bgCanvas,
      display: 'flex',
      justifyContent: 'center',
    }}
  >
    <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column' }}>
      {children}
    </Box>
  </Box>
)

/**
 * The top bar: an optional back affordance, a title, and one trailing slot.
 *
 * It is sticky because these screens scroll and the design keeps the title and
 * the way back visible the whole time.
 *
 * Back comes in two forms and they are not interchangeable. `backTo` renders a
 * real link, which can be middle-clicked, copied and opened in a new tab, and
 * is what you want when "back" means a specific screen. `onBack` is for when it
 * means "undo whatever got me here" — closing a step, popping history — where
 * there is no URL to point at. Prefer `backTo` when you know the destination.
 */
export const TopBar = ({
  title,
  subtitle,
  backTo,
  onBack,
  trailing,
}: {
  title: string
  subtitle?: string
  backTo?: string
  onBack?: () => void
  trailing?: ReactNode
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 2,
      px: 2,
      py: 1.5,
      bgcolor: YALO_SEMANTIC.bgCanvas,
      borderBottom: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
    }}
  >
    {backTo ? (
      <IconButton component={RouterLink} to={backTo} aria-label="Volver" sx={{ ml: -1 }}>
        ←
      </IconButton>
    ) : (
      onBack && (
        <IconButton onClick={onBack} aria-label="Volver" sx={{ ml: -1 }}>
          ←
        </IconButton>
      )
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6" noWrap>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" noWrap>
          {subtitle}
        </Typography>
      )}
    </Box>
    {trailing}
  </Stack>
)

/**
 * The sticky action bar at the bottom of a screen — "Continuar", "Pagar",
 * "Agregar al carrito".
 *
 * It carries its own surface and top border so content scrolling underneath
 * does not appear to touch it, and it sits above the safe-area inset so an
 * iPhone's home indicator does not cover the button.
 */
export const ActionBar = ({ children }: PropsWithChildren) => (
  <Box
    sx={{
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      px: 2,
      pt: 2,
      pb: 'calc(16px + env(safe-area-inset-bottom))',
      bgcolor: YALO_SEMANTIC.bgSurface,
      borderTop: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
      boxShadow: YALO_SHADOW.md,
    }}
  >
    {children}
  </Box>
)

/** A titled block of a screen. The eyebrow is the design's section label. */
export const Section = ({
  eyebrow,
  title,
  action,
  children,
}: PropsWithChildren<{ eyebrow?: string; title?: string; action?: ReactNode }>) => (
  <Box sx={{ px: 2, py: 2 }}>
    {(eyebrow || title || action) && (
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          {eyebrow && (
            <Typography variant="overline" color="text.secondary" display="block">
              {eyebrow}
            </Typography>
          )}
          {title && <Typography variant="h6">{title}</Typography>}
        </Box>
        {action}
      </Stack>
    )}
    {children}
  </Box>
)

/**
 * A horizontally scrolling row — stories, categories, similar products.
 *
 * The scrollbar is hidden because on the design's target (a phone webview)
 * there is none, and a desktop scrollbar inside a 480px column reads as a
 * defect rather than an affordance.
 */
export const ScrollRow = ({ children }: PropsWithChildren) => (
  <Stack
    direction="row"
    spacing={1.5}
    sx={{
      overflowX: 'auto',
      px: 2,
      pb: 1,
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      '& > *': { flexShrink: 0 },
    }}
  >
    {children}
  </Stack>
)

/** The soft-tinted surface the design uses for muted, nested blocks. */
export const SunkenBox = ({ children }: PropsWithChildren) => (
  <Box
    sx={{
      bgcolor: YALO_SEMANTIC.bgSunken,
      borderRadius: `${YALO_RADIUS.lg}px`,
      p: 2,
    }}
  >
    {children}
  </Box>
)
